import { useState } from 'react';
import {
    Avatar,
    Button,
    debounce,
    DebounceTimeoutValues,
    DropdownItem,
    IconName,
    ReducerActionType,
    StackItem,
    tableStateReducer,
    useTableState,
} from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { RoleGetResult, SimpleUser, UserProfileSearchResult } from '@mews-commander/data-access';
import { compactArray } from '@mews/fp';
import { Loader, Select } from '@mews-commander/ui';
import { useGetAvatarSrc } from '@mews-commander/utils';
import { useAddRoleUsers, useAddRoleUsersData } from './queries';

export const AssignUsersDialog = ({
    role,
    onClose,
    active,
}: {
    role: RoleGetResult;
    onClose: () => void;
    active: boolean;
}) => {
    const [tableState, setTableState] = useTableState<UserProfileSearchResult>();
    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useAddRoleUsersData(
        tableState,
        role.id
    );
    const users = (data?.pages.flatMap((page) => page.data.map(({ userProfile }) => userProfile.value)) ||
        []) as SimpleUser[];

    const { mutate: assignUsers, isPending: isAssignPending } = useAddRoleUsers(role.name);
    const [selectedUser, selectUser] = useState<SimpleUser>();
    const getAvatarSrc = useGetAvatarSrc();

    const hasSelectedUserLoaded = users.find((user) => user.id === selectedUser?.id);
    const finalUsers: SimpleUser[] = compactArray([hasSelectedUserLoaded ? null : selectedUser, ...users]);

    const selectOptions = finalUsers.map(({ email, id, fullName, imageId }) => (
        <DropdownItem
            data-test-select-item-value={id}
            key={id}
            value={id}
            label={fullName}
            description={email}
            leftDetail={<Avatar title={fullName || email} src={getAvatarSrc(imageId)} size="medium" />}
        />
    ));

    const onSearch = debounce((search: string) => {
        setTableState((state) =>
            tableStateReducer(state, {
                type: ReducerActionType.GlobalSearch,
                payload: { search },
            })
        );
    }, DebounceTimeoutValues.textInput);

    const onSubmit = () =>
        assignUsers(
            {
                userIds: [selectedUser?.id as string],
                roleId: role.id,
            },
            { onSuccess: onClose }
        );

    const handleLoadMoreClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (isFetching || isFetchingNextPage) return;

        void fetchNextPage();
    };

    const content = (
        <Select
            label={<LocalizedText textKey={TextKey.RoleEditAssignLabel} />}
            // @ts-expect-error Select has bad typing
            loader={isFetching}
            selected={selectedUser?.id}
            onChange={(value: string | null) => {
                selectUser(finalUsers.find(({ id }) => id === value));
            }}
            onSearchChange={onSearch}
            bottomCaption={<LocalizedText textKey={TextKey.RoleEditAssignHint} />}
            iconName={IconName.Search}
            size="medium"
        >
            {selectOptions}
            {hasNextPage ? (
                <StackItem padding="100">
                    <Button variant="ghost" onClick={handleLoadMoreClick} wide type="submit">
                        {isFetchingNextPage ? <Loader size="small" /> : <LocalizedText textKey={TextKey.More} />}
                    </Button>
                </StackItem>
            ) : null}
        </Select>
    );

    return (
        <FloatingPanel
            active={active}
            title={<LocalizedText textKey={TextKey.RoleEditAssignTitle} />}
            footer={{
                primary: (
                    <Button
                        type="submit"
                        variant="primary"
                        onClick={onSubmit}
                        size="medium"
                        disabled={selectedUser?.id == null}
                    >
                        <LocalizedText textKey={TextKey.RoleEditAssignButton} />
                    </Button>
                ),
                secondary: (
                    <Button type="button" variant="tertiary" onClick={onClose} size="medium">
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
            }}
            onClose={onClose}
            loading={isAssignPending}
            size="small"
        >
            {content}
        </FloatingPanel>
    );
};
